import React, { useEffect, useState } from 'react';
import { IconButton, Badge } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../context/AuthProvider';
import { apiClient } from '../../helper/api';
import { AppRoutes } from '../../helper/appRoutes';

/** Custom event name: dispatch this after inbox items are marked read/unread/archived/unarchived so the bell count updates. */
export const CLIENT_INBOX_UPDATED_EVENT = 'clientInboxUpdated';

/** Hook to fetch and subscribe to client inbox unread count. Use for Inbox badge/counter in nav and home. */
export function useInboxUnreadCount(): number {
  const { user } = useAuth();
  const token = user?.token;
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = React.useCallback(() => {
    if (!token) return;
    apiClient('/clientInbox/getUnreadCount', {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((res: any) => {
        const n = res?.data?.unreadCount;
        setUnreadCount(typeof n === 'number' && n >= 0 ? n : 0);
      })
      .catch(() => setUnreadCount(0));
  }, [token]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60 * 1000);
    const onInboxUpdated = () => fetchUnreadCount();
    window.addEventListener(CLIENT_INBOX_UPDATED_EVENT, onInboxUpdated);
    return () => {
      clearInterval(interval);
      window.removeEventListener(CLIENT_INBOX_UPDATED_EVENT, onInboxUpdated);
    };
  }, [fetchUnreadCount]);

  return unreadCount;
}

/** In-portal inbox notification bell with unread count. Always visible; independent of email preference toggles. */
export interface InboxNotificationBellProps {
  /** Icon color for light (black) vs dark (white) header */
  variant?: 'black' | 'white';
  /** Called when bell is clicked (before navigate). Use to close nav drawer etc. */
  onClick?: () => void;
  title?: string;
}

export const InboxNotificationBell: React.FC<InboxNotificationBellProps> = ({
  variant = 'black',
  onClick,
  title = 'Inbox',
}) => {
  const navigate = useNavigate();
  const unreadCount = useInboxUnreadCount();

  const handleClick = () => {
    onClick?.();
    navigate(AppRoutes.inbox);
  };

  const iconColor = variant === 'white' ? '#ffffff' : '#000000';

  return (
    <IconButton
      onClick={handleClick}
      sx={{
        color: iconColor,
        '&:hover': {
          backgroundColor: variant === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
        },
      }}
      title={title}
      aria-label={title}
    >
      <Badge badgeContent={unreadCount > 0 ? unreadCount : 0} color="error">
        <NotificationsIcon sx={{ fontSize: 24 }} />
      </Badge>
    </IconButton>
  );
};
